import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UnreadMessagesBanner() {
  const [unreadData, setUnreadData] = useState({ total_unread: 0, by_payment: [] });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUnreadMessages();
    
    // Poll every 15 seconds for new messages
    const interval = setInterval(fetchUnreadMessages, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/chat/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setUnreadData(response.data);
      
      // If there are new messages, reset dismissed state
      if (response.data.total_unread > 0 && unreadData.total_unread === 0) {
        setIsDismissed(false);
      }
    } catch (error) {
      console.error('Error fetching unread messages:', error);
    }
  };

  const handleGoToPayment = (paymentId) => {
    navigate(`/payments/${paymentId}`);
    setIsExpanded(false);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    setIsExpanded(false);
  };

  // Don't show if no unread messages or dismissed
  if (unreadData.total_unread === 0 || isDismissed) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down" data-testid="unread-messages-banner">
      {/* Main Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <MessageCircle className="h-6 w-6 animate-pulse" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadData.total_unread > 9 ? '9+' : unreadData.total_unread}
                </span>
              </div>
              <div>
                <p className="font-semibold">
                  {unreadData.total_unread === 1 
                    ? '¡Tienes 1 mensaje sin leer!' 
                    : `¡Tienes ${unreadData.total_unread} mensajes sin leer!`}
                </p>
                {unreadData.by_payment.length === 1 && (
                  <p className="text-sm text-blue-100">
                    De: {unreadData.by_payment[0].last_sender} - {unreadData.by_payment[0].proveedor_nombre}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {unreadData.by_payment.length === 1 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleGoToPayment(unreadData.by_payment[0].payment_id)}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  data-testid="go-to-message-btn"
                >
                  Ver Mensaje
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  data-testid="expand-messages-btn"
                >
                  {isExpanded ? (
                    <>Ocultar <ChevronUp className="ml-1 h-4 w-4" /></>
                  ) : (
                    <>Ver Todos <ChevronDown className="ml-1 h-4 w-4" /></>
                  )}
                </Button>
              )}
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-blue-500 rounded-full transition-colors"
                title="Cerrar (volverá a aparecer con nuevos mensajes)"
                data-testid="dismiss-banner-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded List */}
      {isExpanded && unreadData.by_payment.length > 1 && (
        <div className="bg-white border-b border-slate-200 shadow-md">
          <div className="container mx-auto px-4 py-3">
            <p className="text-sm text-slate-500 mb-2">Mensajes pendientes por pago:</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {unreadData.by_payment.map((item) => (
                <div
                  key={item.payment_id}
                  onClick={() => handleGoToPayment(item.payment_id)}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                  data-testid={`unread-payment-${item.payment_id}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 rounded-full p-2">
                      <MessageCircle className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{item.proveedor_nombre}</p>
                      <p className="text-sm text-slate-500">
                        {item.count} mensaje{item.count > 1 ? 's' : ''} de {item.last_sender}
                      </p>
                    </div>
                  </div>
                  <span className="bg-blue-600 text-white text-xs font-bold rounded-full px-2 py-1">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
